'use client'

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const PropsSchema = z.object({
	units: z.object({
		selectorId: z.string().optional(),
		type: z.union([
			z.literal('leaderboard_atf'),
			z.literal('leaderboard_btf'),
			z.literal('med_rect_atf'),
			z.literal('med_rect_btf'),
			z.literal('bottom_rail'),
			z.literal('standard_iab_cntr1'),
			z.literal('standard_iab_cntr7'),
			z.literal('standard_iab_cntr8'),
			z.literal('standard_iab_foot1'),
			z.literal('standard_iab_left2'),
			z.literal('standard_iab_rght2'),
			z.literal('standard_iab_rght3'),
			z.literal('standard_iab_rght4'),
		])
	})
		.superRefine(({ selectorId, type }, ctx) => {
			if(type !== 'bottom_rail' && (!selectorId || selectorId.trim() === '')) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "selectorId is required unless type is 'bottom_rail'",
					path: ['selectorId'],
				});
			}
		})
		.array(),
	tags: z.string().array().optional(),
	setIsRampLoaded: z.function().args(z.boolean()).returns(z.void()).optional(),
	playwirePartnerId: z.string().nullish(),
})

type Props = z.infer<typeof PropsSchema>

export const RampScript = (props: Props) => {
	const [rampComponentLoaded, setRampComponentLoaded] = useState(false);
	const pathname = usePathname(); // next/navigation hook to get the current location

	const { units, tags, setIsRampLoaded, playwirePartnerId } = props
	
	useEffect(() => {
		if (!process.env.NEXT_PUBLIC_RAMP_PUB_ID || !process.env.NEXT_PUBLIC_RAMP_WEBSITE_ID) {
			console.log('Missing Publisher Id and Website Id');
			return;
		}

		if(document.querySelector(`script[src='https://cdn.intergient.com/${process.env.NEXT_PUBLIC_RAMP_PUB_ID}/${playwirePartnerId ? playwirePartnerId : process.env.NEXT_PUBLIC_RAMP_WEBSITE_ID}/ramp.js']`)) {
			setRampComponentLoaded(true);
			window.ramp.que.push(() => {
				if(tags && tags.length > 0) {
					tags.forEach((t) => window.ramp.addTag(t));
				}
				window.ramp.addUnits(units).then(() => {
					window.ramp.displayUnits()
				});
				window.ramp.spaNewPage();
			})
			if (setIsRampLoaded) {
				setIsRampLoaded(true);
			}
			return
		}

		if (!rampComponentLoaded) {
			setRampComponentLoaded(true);
			window.ramp = window.ramp || {};
			window.ramp.que = window.ramp.que || [];
			window.ramp.passiveMode = true;
			window.ramp.custom_tags = ["", "", "", "www.playdia.com"]

			// Load the Ramp configuration script
			const configScript = document.createElement('script');
			configScript.src = `https://cdn.intergient.com/${process.env.NEXT_PUBLIC_RAMP_PUB_ID}/${playwirePartnerId ? playwirePartnerId : process.env.NEXT_PUBLIC_RAMP_WEBSITE_ID}/ramp.js`;
			document.body.appendChild(configScript);

			configScript.onload = () => {
				window.ramp.que.push(() => {
					if(tags && tags.length > 0) {
						tags.forEach((t) => window.ramp.addTag(t));
					}
					window.ramp.addUnits(units).then(() => {
						window.ramp.displayUnits()
					});

					window.ramp.spaNewPage();
				});
				if (setIsRampLoaded) {
					setIsRampLoaded(true);
				}
			};
		}

		// Cleanup function to handle component unmount
		return () => {
			window.ramp.que.push(() => {
				window.ramp.spaNewPage();
			});
			console.log("RampScript unmounted, resetting state");
		};
	}, [pathname, units]);

	return null;
}
